import { useEffect, useState } from 'react';

export interface ShortcutGroup {
  category: string;
  items: Array<{ key: string; description: string }>;
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    category: 'Navigation & Viewport',
    items: [
      { key: 'Right-Click + WASD', description: 'Free-fly camera movement' },
      { key: 'Mouse Wheel', description: 'Zoom in / out' },
      { key: 'F', description: 'Focus camera on selected object' },
      { key: 'Shift + Mouse Move', description: 'Precision orbit / pan' },
    ],
  },
  {
    category: 'Selection & Manipulation',
    items: [
      { key: 'V', description: 'Select / Inspect tool' },
      { key: 'G', description: 'Translate (Move) tool' },
      { key: 'R', description: 'Rotate tool' },
      { key: 'S', description: 'Scale tool' },
      { key: 'Q / E', description: 'Rotate selection by 45°' },
      { key: 'Arrow Keys', description: 'Nudge object along grid' },
      { key: 'Shift + Arrows', description: 'Fast nudge (10x step)' },
    ],
  },
  {
    category: 'Document & History',
    items: [
      { key: 'Ctrl / Cmd + Z', description: 'Undo action' },
      { key: 'Ctrl / Cmd + Y', description: 'Redo action' },
      { key: 'Ctrl / Cmd + S', description: 'Save scene snapshot to disk' },
      { key: 'Ctrl / Cmd + A', description: 'Select all floor objects' },
      { key: 'Ctrl / Cmd + D', description: 'Duplicate selection' },
      { key: 'Ctrl / Cmd + C / V', description: 'Copy / Paste at cursor' },
      { key: 'Delete / Backspace', description: 'Delete selection' },
      { key: 'Escape', description: 'Clear selection / cancel placement' },
    ],
  },
  {
    category: 'Code',
    items: [
      { key: 'Ctrl / Cmd + Enter', description: 'Run the open script in the sandbox' },
      { key: 'Ctrl / Cmd + S', description: 'Save the open script now (edits also autosave)' },
      { key: 'Shift + Alt + F', description: 'Format the open script' },
      { key: 'Ctrl / Cmd + F', description: 'Find and replace in the open script' },
      { key: 'Ctrl / Cmd + Shift + O', description: 'Go to a function or declaration' },
      { key: 'Ctrl / Cmd + G', description: 'Go to line' },
      { key: 'F8', description: 'Jump to the next problem' },
      { key: 'F2', description: 'Rename the selected script' },
    ],
  },
  {
    category: 'General & Overlays',
    items: [
      { key: '?', description: 'Toggle this shortcut guide' },
      { key: '`', description: 'Toggle the HollowScript console' },
      { key: 'Ctrl / Cmd + Shift + F', description: 'Toggle distraction-free mode' },
    ],
  },
];

export function ShortcutOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const filteredGroups = SHORTCUT_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter(
      (item) =>
        item.key.toLowerCase().includes(filter.toLowerCase()) ||
        item.description.toLowerCase().includes(filter.toLowerCase()),
    ),
  })).filter((g) => g.items.length > 0);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(4,4,6,0.6)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        className="ss-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 680,
          maxHeight: '85vh',
          padding: 0,
          borderRadius: 16,
          boxShadow: '0 24px 48px rgba(0,0,0,0.35)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--ss-border-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h2 className="ss-display" style={{ fontSize: 18, fontWeight: 600, color: 'var(--ss-text)', margin: 0 }}>
              Keyboard shortcuts
            </h2>
            <span style={{ fontSize: 12, color: 'var(--ss-text-3)', marginTop: 2, display: 'block' }}>
              Soulstack Studio editor controls
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--ss-text-3)',
              fontSize: 20,
              cursor: 'pointer',
              padding: 4,
            }}
          >
            ✕
          </button>
        </div>

        {/* Filter input */}
        <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--ss-border-soft)' }}>
          <input
            type="text"
            className="ss-input"
            placeholder="Search shortcuts (e.g. rotate, save, WASD)…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ width: '100%', fontSize: 13 }}
            autoFocus
          />
        </div>

        {/* Groups */}
        <div style={{ padding: 24, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
          {filteredGroups.length === 0 ? (
            <p style={{ color: 'var(--ss-text-3)', textAlign: 'center', margin: '24px 0', fontSize: 13 }}>
              No matching shortcuts found.
            </p>
          ) : (
            filteredGroups.map((group) => (
              <div key={group.category}>
                <h3
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: 'var(--ss-accent)',
                    marginBottom: 10,
                  }}
                >
                  {group.category}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {group.items.map((item) => (
                    <div
                      key={item.key + item.description}
                      style={{
                        padding: '10px 12px',
                        background: 'var(--ss-hover)',
                        borderRadius: 8,
                        border: '1px solid var(--ss-border-soft)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                      }}
                    >
                      <span className="ss-kbd" style={{ fontFamily: 'var(--ss-mono)', width: 'fit-content' }}>
                        {item.key}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--ss-text-3)' }}>{item.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
